import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CountryPhoneInput from '../components/CountryPhoneInput';
import api from '../services/api';
import './Register.css';

const RegisterTeacher = () => {
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    language: '',
    phone: '',
    consentToPersonalData: false,
  });
  const [error, setError] = useState('');
  const [phoneVerification, setPhoneVerification] = useState({
    code: ['', '', '', ''],
    sending: false,
    verifying: false,
    error: '',
    codeSent: false,
    verified: false,
  });
  const phoneCodeInputRefs = useRef([]);
  const { register } = useAuth();
  const navigate = useNavigate();

  const countries = [
    { value: 'RU', label: 'Россия' },
    { value: 'US', label: 'США' },
    { value: 'GB', label: 'Великобритания' },
    { value: 'DE', label: 'Германия' },
    { value: 'FR', label: 'Франция' },
    { value: 'IT', label: 'Италия' },
    { value: 'ES', label: 'Испания' },
    { value: 'CN', label: 'Китай' },
    { value: 'JP', label: 'Япония' },
    { value: 'KR', label: 'Южная Корея' },
    { value: 'IN', label: 'Индия' },
    { value: 'AU', label: 'Австралия' },
    { value: 'BR', label: 'Бразилия' },
    { value: 'MX', label: 'Мексика' },
    { value: 'NL', label: 'Нидерланды' },
    { value: 'BE', label: 'Бельгия' },
    { value: 'CH', label: 'Швейцария' },
    { value: 'SE', label: 'Швеция' },
    { value: 'NO', label: 'Норвегия' },
    { value: 'DK', label: 'Дания' },
    { value: 'FI', label: 'Финляндия' },
    { value: 'PL', label: 'Польша' },
    { value: 'CZ', label: 'Чехия' },
    { value: 'HU', label: 'Венгрия' },
    { value: 'GR', label: 'Греция' },
    { value: 'PT', label: 'Португалия' },
    { value: 'IE', label: 'Ирландия' },
    { value: 'CY', label: 'Кипр' },
  ];

  const languages = [
    { value: 'ru', label: 'Русский' },
    { value: 'en', label: 'Английский язык' },
    { value: 'de', label: 'Немецкий' },
    { value: 'fr', label: 'Французский' },
    { value: 'es', label: 'Испанский' },
    { value: 'it', label: 'Итальянский' },
    { value: 'zh', label: 'Китайский' },
    { value: 'ja', label: 'Японский' },
    { value: 'ko', label: 'Корейский' },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
    // Сбрасываем верификацию при изменении телефона
    if (phoneVerification.codeSent || phoneVerification.verified) {
      setPhoneVerification({
        code: ['', '', '', ''],
        sending: false,
        verifying: false,
        error: '',
        codeSent: false,
        verified: false,
      });
    }
  };

  const handlePhoneCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }
    const newCode = [...phoneVerification.code];
    newCode[index] = value;
    setPhoneVerification({ ...phoneVerification, code: newCode, error: '' });
    
    if (value && index < 3) {
      phoneCodeInputRefs.current[index + 1]?.focus();
    }
  };

  const handlePhoneCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !phoneVerification.code[index] && index > 0) {
      phoneCodeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleSendPhoneCode = async () => {
    if (!formData.phone || formData.phone.trim().length < 5) {
      setPhoneVerification({
        ...phoneVerification,
        error: 'Введите корректный номер телефона'
      });
      return;
    }
    
    setPhoneVerification({ ...phoneVerification, sending: true, error: '' });
    try {
      const phoneNumber = formData.phone.trim();
      const response = await api.post('/auth/verify/phone/send/', { phone: phoneNumber });
      setPhoneVerification({ 
        ...phoneVerification, 
        sending: false, 
        error: '',
        codeSent: true
      });
      phoneCodeInputRefs.current[0]?.focus();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Ошибка отправки кода';
      setPhoneVerification({
        ...phoneVerification,
        sending: false,
        error: errorMessage,
        codeSent: false
      });
    }
  };

  const handleVerifyPhoneCode = async () => {
    const codeString = phoneVerification.code.join('');
    if (codeString.length !== 4) {
      setPhoneVerification({ ...phoneVerification, error: 'Введите 4-значный код' });
      return;
    }
    
    setPhoneVerification({ ...phoneVerification, verifying: true, error: '' });
    try {
      const response = await api.post('/auth/verify/phone/verify/', { 
        code: codeString,
        phone: formData.phone 
      });
      if (response.data.message) {
        setPhoneVerification({ 
          ...phoneVerification,
          verifying: false,
          verified: true,
          error: ''
        });
      }
    } catch (error) {
      setPhoneVerification({
        ...phoneVerification,
        verifying: false,
        error: error.response?.data?.error || 'Неверный код'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.consentToPersonalData) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }

    if (!phoneVerification.verified) {
      setError('Необходимо подтвердить номер телефона');
      return;
    }

    // Формируем данные для отправки (без паролей)
    const registrationData = {
      username: formData.username || formData.email.split('@')[0],
      email: formData.email,
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone,
      bio: formData.language ? `Язык: ${formData.language}` : '',
    };

    const result = await register(registrationData, 'teacher');
    if (result.success) {
      // Для учителей всегда переходим в анкету без верификации email
      navigate('/teacher-questionnaire');
    } else {
      setError(result.error || 'Ошибка регистрации');
    }
  };

  return (
    <div className="auth-page">
      <Header />
      <div className="register-container">
        <div className="register-box">
          <h2>Регистрация преподавателя</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Имя</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Фамилия</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Язык</label>
              <select
                name="language"
                value={formData.language}
                onChange={handleChange}
                required
                className="form-select"
              >
                <option value="">Выберите язык</option>
                {languages.map(lang => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Телефон</label>
              <CountryPhoneInput
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="Введите номер телефона"
                disabled={phoneVerification.verified}
              />
              {!phoneVerification.codeSent && !phoneVerification.verified && (
                <button
                  type="button"
                  onClick={handleSendPhoneCode}
                  disabled={phoneVerification.sending || !formData.phone || formData.phone.trim().length < 5}
                  className="btn-send-phone-code"
                  style={{ marginTop: '0.5rem' }}
                >
                  {phoneVerification.sending ? 'Отправка...' : 'Отправить код подтверждения'}
                </button>
              )}
              
              {phoneVerification.codeSent && !phoneVerification.verified && (
                <div className="phone-verification" style={{ marginTop: '1rem' }}>
                  <label>Код подтверждения</label>
                  <div className="phone-code-inputs">
                    {phoneVerification.code.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (phoneCodeInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handlePhoneCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handlePhoneCodeKeyDown(index, e)}
                        className="phone-code-input"
                        autoComplete="off"
                      />
                    ))}
                  </div>
                  {phoneVerification.error && (
                    <div className="error" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                      {phoneVerification.error}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleVerifyPhoneCode}
                      disabled={phoneVerification.verifying || phoneVerification.code.join('').length !== 4}
                      className="btn-primary"
                      style={{ flex: 1 }}
                    >
                      {phoneVerification.verifying ? 'Проверка...' : 'Подтвердить'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSendPhoneCode}
                      disabled={phoneVerification.sending}
                      className="btn-secondary"
                      style={{ flex: 1 }}
                    >
                      {phoneVerification.sending ? 'Отправка...' : 'Отправить повторно'}
                    </button>
                  </div>
                </div>
              )}
              
              {phoneVerification.verified && (
                <div style={{ marginTop: '0.5rem', color: '#10b981', fontSize: '0.875rem' }}>
                  ✓ Телефон подтвержден
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="consentToPersonalData"
                  checked={formData.consentToPersonalData}
                  onChange={handleChange}
                  required
                />
                <span>
                  Я согласен на <a href="/static/documents/privacy_policy.pdf" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>обработку персональных данных</a>
                </span>
              </label>
            </div>
            {error && <div className="error">{error}</div>}
            <button 
              type="submit" 
              className="btn-primary"
              disabled={!phoneVerification.verified}
            >
              Перейти к анкете
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterTeacher;

